import { useLingui } from "@lingui/react/macro";
import {
  Brain,
  Building2,
  CloudDownload,
  Cpu,
  CreditCard,
  Gauge,
  KeyRound,
  MessageCircle,
  Monitor,
  SlidersHorizontal,
  User,
  Users,
  Wallet,
} from "lucide-react";
import type { ComponentType } from "react";
import type { SettingsSection } from "./shell-overlay";

export type SettingsNavItem = {
  id: SettingsSection;
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
};

export type SettingsNavGroup = { label: string; items: SettingsNavItem[] };

type Available = {
  /** Company is a Space admin's page, offered once the server answers for this admin. */
  company?: boolean;
  messaging: boolean;
  wallet: boolean;
  computer: boolean;
  billing: boolean;
  updates: boolean;
};

/** The settings sections this deployment offers, grouped in nav order; Space opens from its own menu. */
export function useSettingsNavGroups(available: Available): SettingsNavGroup[] {
  const { t } = useLingui();
  return [
    {
      label: t`You`,
      items: [
        { id: "general", label: t`Account`, icon: User },
        { id: "members", label: t`Members`, icon: Users },
        ...(available.company
          ? [{ id: "company" as const, label: t`Company`, icon: Building2 }]
          : []),
        { id: "preferences", label: t`Preferences`, icon: SlidersHorizontal },
        { id: "usage", label: t`Usage`, icon: Gauge },
        { id: "vault", label: t`Vault`, icon: KeyRound },
        ...(available.messaging
          ? [{ id: "messaging" as const, label: t`Messaging`, icon: MessageCircle }]
          : []),
        ...(available.wallet ? [{ id: "wallet" as const, label: t`Wallet`, icon: Wallet }] : []),
        ...(available.computer
          ? [{ id: "computer" as const, label: t`Computer`, icon: Monitor }]
          : []),
        ...(available.billing
          ? [{ id: "billing" as const, label: t`Billing`, icon: CreditCard }]
          : []),
        ...(available.updates
          ? [{ id: "updates" as const, label: t`Updates`, icon: CloudDownload }]
          : []),
      ],
    },
    {
      label: t`AI`,
      items: [
        { id: "models", label: t`Models`, icon: Cpu },
        { id: "memory", label: t`Memory`, icon: Brain },
      ],
    },
  ];
}

/** The same sections as one list, for the command palette. */
export function useSettingsNavItems(available: Available): SettingsNavItem[] {
  return useSettingsNavGroups(available).flatMap((group) => group.items);
}
